/**
	Format price coming from Shopify to be rendered in the DOM

	Usage :
	- Liquid :
	<script>
	window.money_format = '{{ shop.money_format }}'
	</script>

	- Alpine :
	x-text='$useFormatPrice(1000)'
	x-text='$useFormatPrice(1000, "${{amount}}")'
 */

const shopifyFormatCurrency = (cents, formatString) => {
	const placeholderRegex = /{{\s*(\w+)\s*}}/

	/**
	 * Formats a number of cents into a currency string using the provided
	 * precision, thousands separator, and decimal separator.
	 * @param {number} number - The number of cents to format.
	 * @param {number} [precision=2] - The number of decimal places to include.
	 * @param {string} [thousands=","] - The character to use as the thousands separator.
	 * @param {string} [decimal="."] - The character to use as the decimal separator.
	 * @returns {string} The formatted currency string.
	 */
	const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
		if (isNaN(number) || number == null) {
			return '0'
		}
		const numString = (number / 100.0).toFixed(precision)
		const parts = numString.split('.')
		const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`)
		const cents = parts[1] ? decimal + parts[1] : ''
		return dollars + cents
	}

	return formatString.replace(placeholderRegex, (match, placeholder) => {
		switch (placeholder) {
			case 'amount':
				return formatWithDelimiters(cents, 2)
			case 'amount_no_decimals':
				return formatWithDelimiters(cents, 0)
			case 'amount_with_comma_separator':
				return formatWithDelimiters(cents, 2, '.', ',')
			case 'amount_no_decimals_with_comma_separator':
				return formatWithDelimiters(cents, 0, '.', ',')
			case 'amount_with_apostrophe_separator':
				return formatWithDelimiters(cents, 2, "'", '.')
			case 'amount_no_decimals_with_space_separator':
				return formatWithDelimiters(cents, 0, ' ')
			case 'amount_with_space_separator':
				return formatWithDelimiters(cents, 2, ' ', ',')
			case 'amount_with_period_and_space_separator':
				return formatWithDelimiters(cents, 2, ' ', '.')
			default:
				return match
		}
	})
}

export default {
	name: 'useFormatPrice',
	callback: () => (cents: string | number, format?: string) => {
		const numericCents = typeof cents === 'string' ? Number(cents.replace('.', '')) : cents

		// eslint-disable-next-line no-template-curly-in-string
		const formatString = format || window.money_format || '${{amount}}'

		// Remove HTML tags from the money format if needed
		const formatStringWithoutTags = formatString?.replace(/<[^>]*>?/gm, '')

		return shopifyFormatCurrency(numericCents, formatStringWithoutTags)
	}
}
